/**
 * ProcessoListCard Component
 *
 * Story: 11.5 - Processos Atribuídos
 * Compact card for processo list display
 */
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Calendar } from "lucide-react";
import {
  ProcessoListCardProps,
  PROCESSO_STATUS_LABELS,
  PROCESSO_STATUS_COLORS,
  PRIORIDADE_LABELS,
  PRIORIDADE_COLORS,
} from "../../types/processo-parceiro";
import PrazoIndicator from "./PrazoIndicator";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

const ProcessoListCard: React.FC<ProcessoListCardProps> = ({ processo, onClick }) => {
  // Map status to Badge variant
  const statusColorMap: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    aguardando_documentos: "outline",
    em_analise: "secondary",
    aguardando_retorno: "outline",
    em_andamento: "default",
    concluido: "secondary",
    cancelado: "outline",
  };

  // Map prioridade to Badge variant
  const prioridadeColorMap: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    baixa: "outline",
    normal: "secondary",
    alta: "destructive",
  };

  return (
    <Card
      className="h-full transition-all hover:shadow-lg hover:-translate-y-1 cursor-pointer"
      onClick={() => onClick(processo)}
    >
      <CardContent className="p-4">
        {/* Header: Número + Prioridade */}
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            <h3 className="text-lg font-bold">{processo.numero}</h3>
          </div>
          <Badge variant={prioridadeColorMap[processo.prioridade]} className="text-xs">
            {PRIORIDADE_LABELS[processo.prioridade]}
          </Badge>
        </div>

        {/* Título */}
        <p className="font-medium text-sm mb-2 truncate">{processo.titulo}</p>

        {/* Status */}
        <div className="mb-3">
          <Badge variant={statusColorMap[processo.status]} className="font-medium text-xs">
            {PROCESSO_STATUS_LABELS[processo.status]}
          </Badge>
        </div>

        {/* Próxima Ação */}
        {processo.proxima_acao_descricao && (
          <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
            <strong>Próxima ação:</strong> {processo.proxima_acao_descricao}
          </p>
        )}

        {/* Prazo + Data Criação */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <PrazoIndicator
            prazo={processo.proxima_acao_prazo}
            status={processo.status}
            isPrazoVencido={processo.is_prazo_vencido}
            diasAtePrazo={processo.dias_ate_prazo}
            showCountdown={false}
          />
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            <span>Criado em {format(new Date(processo.created_at), "dd/MM/yyyy")}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProcessoListCard;
